import { GenericCard } from 'components/display/Card/Card';
import { FormSectionSimple } from 'components/layout/Section/FormSectionSimple';
import { Button } from 'components/ui/Button';
import { GenericMessage } from 'components/feedback/Message';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { useFormikContext } from 'formik';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FC, useCallback, useEffect, useMemo } from 'react';
import {
  DynamicArrayConfig,
  InputConfigWithUnit,
  ReferentialDropdownConfig
} from 'types/form/componentConfig';
import {
  BorrowerLoanFormFieldNames,
  BorrowerLoanFormValues,
  LoanTypeCodes,
  sumTierDurations
} from './borrowerLoanForm';
import { borrowerLoanInputsConfig } from './borrowerLoanForm.inputs';
import { LoanDurationCalculator } from './LoanDurationCalculator';

interface BorrowerLoanFormLayoutProps {
  isRenego: boolean;
  disabledFields?: Array<Exclude<keyof BorrowerLoanFormValues, 'id'>>;
  showSaveButton?: boolean;
  isSubmitting?: boolean;
  onSave?: () => void;
}

export const BorrowerLoanFormLayout: FC<BorrowerLoanFormLayoutProps> = ({
  isRenego,
  disabledFields,
  showSaveButton,
  isSubmitting,
  onSave
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower, NsKey.Common]);
  const { values, setFieldValue, validateForm } =
    useFormikContext<BorrowerLoanFormValues>();

  const loanType = useMemo(() => {
    if (values?.[BorrowerLoanFormFieldNames.LoanType]) {
      return values[BorrowerLoanFormFieldNames.LoanType];
    }
    return undefined;
  }, [values]);

  // Le sélecteur de type de prêt pose la valeur sans re-valider (shouldValidate=false).
  // On force la re-validation au changement de type pour rafraîchir isValid (gating du bouton),
  // sans toucher au `touched` → pas d'erreurs de champ prématurées.
  useEffect(() => {
    validateForm();
  }, [loanType, validateForm]);

  const tieredRate = useMemo(
    () => Boolean(values?.[BorrowerLoanFormFieldNames.TieredRate]),
    [values]
  );

  // Règle métier : la somme des durées des paliers doit égaler la durée totale du prêt.
  // On n'affiche le message qu'une fois qu'au moins un champ de palier a été rempli.
  const tierDurationMismatch = useMemo(() => {
    if (loanType !== LoanTypeCodes.Palier) return null;
    const tiers = values?.[BorrowerLoanFormFieldNames.Tiers] ?? [];

    const tiersStarted = tiers.some(
      (tier) =>
        Number(tier?.[BorrowerLoanFormFieldNames.TierDuration]) ||
        Number(tier?.[BorrowerLoanFormFieldNames.TierAmount]) ||
        Number(tier?.[BorrowerLoanFormFieldNames.TierRate])
    );
    if (!tiersStarted) return null;

    const totalDuration =
      Number(values?.[BorrowerLoanFormFieldNames.TotalDuration]) || 0;
    const sumTiers = sumTierDurations(tiers);
    if (sumTiers === totalDuration) return null;
    return { sumTiers, totalDuration };
  }, [loanType, values]);

  // INFINE force un type de différé en coulisses (champ masqué visuellement,
  // valeur posée pour satisfaire la validation Yup).
  useEffect(() => {
    if (loanType === LoanTypeCodes.Infine) {
      setFieldValue(BorrowerLoanFormFieldNames.DefermentType, 'differe-partiel');
    }
  }, [loanType, setFieldValue]);

  // Config dérivée du type de prêt — construite par copie immutable (jamais de
  // mutation de l'objet partagé borrowerLoanInputsConfig).
  const inputsConfig = useMemo(() => {
    const isInfine = loanType === LoanTypeCodes.Infine;
    const isCreditBail = loanType === LoanTypeCodes.CreditBail;
    const isPalier = loanType === LoanTypeCodes.Palier;

    // Différé : INFINE masque l'option "aucun-differe".
    const defermentTypeConfig = {
      ...(borrowerLoanInputsConfig.defermentType as ReferentialDropdownConfig),
      omitKeys: isInfine ? ['aucun-differe'] : undefined
    };

    // Crédit-bail : LoanAmount exposé sous le label "Montant de la base locative".
    const loanAmountConfig = {
      ...(borrowerLoanInputsConfig.loanAmount as InputConfigWithUnit),
      label: isCreditBail
        ? 'BORROWER_LOAN_FORM.LABELS.LEASE_BASE_AMOUNT'
        : 'BORROWER_LOAN_FORM.LABELS.LOAN_AMOUNT',
      placeholder: isCreditBail
        ? 'BORROWER_LOAN_FORM.PLACEHOLDERS.LEASE_BASE_AMOUNT'
        : 'BORROWER_LOAN_FORM.PLACEHOLDERS.LOAN_AMOUNT'
    };

    // Taux global (PALIER) : libellé "Taux global", requis hors mode "taux par palier".
    const rateConfig = borrowerLoanInputsConfig[
      BorrowerLoanFormFieldNames.InterestRatePercentage
    ] as InputConfigWithUnit;

    // Colonne "taux" des paliers : visible uniquement en mode "taux par palier".
    const tiersConfig = borrowerLoanInputsConfig[
      BorrowerLoanFormFieldNames.Tiers
    ] as DynamicArrayConfig;
    const tiersFields = tieredRate
      ? tiersConfig.fields
      : tiersConfig.fields.filter(
          (field) => field.name !== BorrowerLoanFormFieldNames.TierRate
        );

    return {
      ...borrowerLoanInputsConfig,
      [BorrowerLoanFormFieldNames.DefermentType]: defermentTypeConfig,
      [BorrowerLoanFormFieldNames.LoanAmount]: loanAmountConfig,
      [BorrowerLoanFormFieldNames.InterestRatePercentage]: isPalier
        ? { ...rateConfig, label: 'Taux global', required: true }
        : rateConfig,
      [BorrowerLoanFormFieldNames.Tiers]: {
        ...tiersConfig,
        fields: tiersFields
      }
    };
  }, [loanType, tieredRate]);

  const renderField = useCallback(
    (fieldName: Exclude<keyof BorrowerLoanFormValues, 'id'>) => {
      return (
        <InputFactory<Exclude<keyof BorrowerLoanFormValues, 'id'>>
          config={inputsConfig}
          fieldName={fieldName}
          state={values}
          disabledFields={disabledFields}
        />
      );
    },
    [values, inputsConfig, disabledFields]
  );

  // Total duration field with the remaining duration calculator on its right
  // (hidden when the field is disabled). The button column mirrors the FormField
  // label row (same gap and line height) so the button stays level with the
  // input whether or not a validation error shows below it.
  const renderTotalDurationField = () => {
    if (disabledFields?.includes(BorrowerLoanFormFieldNames.TotalDuration)) {
      return renderField(BorrowerLoanFormFieldNames.TotalDuration);
    }
    return (
      <div className="flex items-start gap-2">
        <div className="flex-1">
          {renderField(BorrowerLoanFormFieldNames.TotalDuration)}
        </div>
        <div className="flex flex-col gap-1.5">
          <span aria-hidden="true" className="text-sm leading-6">
            &nbsp;
          </span>
          <LoanDurationCalculator
            onApply={(months) =>
              setFieldValue(BorrowerLoanFormFieldNames.TotalDuration, months)
            }
          />
        </div>
      </div>
    );
  };

  const defermentType = useMemo(() => {
    if (values?.[BorrowerLoanFormFieldNames.DefermentType]) {
      return values[BorrowerLoanFormFieldNames.DefermentType];
    }
    return undefined;
  }, [values]);

  return (
    <div className="font-lato flex flex-col gap-4">
      {/* Section Informations de base */}
      <FormSectionSimple
        title={t('BORROWER_LOAN_FORM.SECTIONS.BASIC_INFO')}
        titleAside={
          showSaveButton ? (
            <Button
              color="primary"
              onClick={onSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('COMMON.SAVING') : t('COMMON.SAVE')}
            </Button>
          ) : undefined
        }
      >
        <>
          <div className="col-span-3">
            {renderField(BorrowerLoanFormFieldNames.LoanAmount)}
          </div>
          {isRenego && (
            <div className="col-span-3">
              {renderField(BorrowerLoanFormFieldNames.InsuranceAmount)}
            </div>
          )}
          <div className="col-span-6">
            {renderField(BorrowerLoanFormFieldNames.LoanType)}
          </div>
          {loanType === LoanTypeCodes.Palier && (
            <div className="col-span-3">
              {renderTotalDurationField()}
            </div>
          )}
        </>
      </FormSectionSimple>

      {/* Section Taux d'intérêt et durée — exclue pour PALIER, PTZ et CRÉDIT-BAIL
          (ces types embarquent leurs propres champs taux/durée/différé dans leur
          section dédiée plus bas). */}
      {!(
        loanType === LoanTypeCodes.Palier ||
        loanType === LoanTypeCodes.Ptz ||
        loanType === LoanTypeCodes.CreditBail
      ) && (
        <FormSectionSimple
          title={t('BORROWER_LOAN_FORM.SECTIONS.RATE_AND_DURATION')}
        >
          <>
            <div className="col-span-3">
              {renderField(BorrowerLoanFormFieldNames.InterestRateType)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerLoanFormFieldNames.InterestRatePercentage)}
            </div>
            <div className="col-span-3">
              {renderTotalDurationField()}
            </div>
            {/*
              Bloc différé :
              - INFINE : type ET durée masqués (le différé est conceptuellement
                porté par toute la durée du prêt ; le type est forcé à
                'differe-partiel' en coulisses pour satisfaire le schéma Yup).
              - RELAIS et autres types : type toujours visible (avec l'option
                "aucun-differe"), durée affichée seulement si type ≠ aucun-differe.
            */}
            {loanType !== LoanTypeCodes.Infine && (
              <div className="col-span-6 grid grid-cols-6 gap-4 border-t pt-4 mt-4">
                <div className="col-span-3">
                  {renderField(BorrowerLoanFormFieldNames.DefermentType)}
                </div>
                {defermentType !== 'aucun-differe' && (
                  <div className="col-span-3">
                    {renderField(BorrowerLoanFormFieldNames.DefermentPeriod)}
                  </div>
                )}
              </div>
            )}
          </>
        </FormSectionSimple>
      )}

      {/* Section Taux d'intérêt */}
      {loanType === LoanTypeCodes.Ptz && (
        <FormSectionSimple title={t('BORROWER_LOAN_FORM.SECTIONS.DURATION')}>
          <>
            <div className="col-span-3">
              {renderTotalDurationField()}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerLoanFormFieldNames.DefermentPeriod)}
            </div>
          </>
        </FormSectionSimple>
      )}

      {/* Section Crédit-bail
          Le crédit-bail a sa propre section taux/durée/différé (le cartouche
          générique "Taux d'intérêt et durée" est court-circuité plus haut).
          Champs demandés : durée totale, montant du loyer, taux d'intérêt,
          type + durée du différé. Le type de taux n'est pas demandé (envoyé
          en "fixe" par défaut). Durée restante et valeur résiduelle ne sont
          plus demandées. */}
      {loanType === LoanTypeCodes.CreditBail && (
        <FormSectionSimple
          title={t('BORROWER_LOAN_FORM.SECTIONS.LEASE_CREDIT')}
        >
          <>
            <div className="col-span-3">
              {renderTotalDurationField()}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerLoanFormFieldNames.RentAmount)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerLoanFormFieldNames.InterestRatePercentage)}
            </div>
            <div className="col-span-6 grid grid-cols-6 gap-4 border-t pt-4 mt-4">
              <div className="col-span-3">
                {renderField(BorrowerLoanFormFieldNames.DefermentType)}
              </div>
              {defermentType !== 'aucun-differe' && (
                <div className="col-span-3">
                  {renderField(BorrowerLoanFormFieldNames.DefermentPeriod)}
                </div>
              )}
            </div>
          </>
        </FormSectionSimple>
      )}

      {/* Section Prêt palier */}
      {loanType === LoanTypeCodes.Palier && (
        <FormSectionSimple title={t('BORROWER_LOAN_FORM.SECTIONS.TIERED_LOAN')}>
          <>
            {tierDurationMismatch && (
              <div className="col-span-6">
                <GenericMessage
                  type="error"
                  size="sm"
                  dismissible={false}
                  message={`La somme des durées des paliers (${tierDurationMismatch.sumTiers} mois) doit être égale à la durée totale du prêt (${tierDurationMismatch.totalDuration} mois).`}
                />
              </div>
            )}

            {/* Sous-bloc Taux d'intérêt : taux unique global ou taux par palier */}
            <div className="col-span-6">
              <GenericCard>
                <GenericCard.Header
                  subtitle={
                    tieredRate
                      ? 'Un taux distinct est défini pour chaque palier.'
                      : "Un taux unique s'applique à l'ensemble du prêt."
                  }
                >
                  <span className="font-semibold text-zinc-800">
                    Taux d'intérêt
                  </span>
                </GenericCard.Header>
                <GenericCard.Body className="flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500">
                      {tieredRate
                        ? 'Repassez en taux unique pour appliquer un seul taux à tous les paliers.'
                        : "Le taux varie d'un palier à l'autre ? Activez les taux par palier."}
                    </span>
                    <div className="shrink-0 whitespace-nowrap">
                      <FormikSwitch
                        name={BorrowerLoanFormFieldNames.TieredRate}
                        label="Taux par palier"
                      />
                    </div>
                  </div>

                  {!tieredRate && (
                    <div className="grid grid-cols-6 gap-4">
                      <div className="col-span-3">
                        {renderField(
                          BorrowerLoanFormFieldNames.InterestRatePercentage
                        )}
                      </div>
                    </div>
                  )}

                  {tieredRate && (
                    <GenericMessage
                      type="info"
                      size="sm"
                      dismissible={false}
                      message="Si le type de cotisation CI (Capital Initial) est sélectionné par la suite, c'est le taux du premier palier qui fait foi pour toute la durée du prêt."
                    />
                  )}
                </GenericCard.Body>
              </GenericCard>
            </div>

            {/* Paliers */}
            <div className="col-span-6">
              {renderField(BorrowerLoanFormFieldNames.Tiers)}
            </div>
          </>
        </FormSectionSimple>
      )}
    </div>
  );
};
